import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash, PencilSimple, Copy, Eye, Star } from "@phosphor-icons/react";
import { toast } from "sonner";

const KINDS = [
  { key: "invoice",      label: "Invoice",      hint: "Bill-to header, item table, totals, T&C — used by Tax Invoice PDF." },
  { key: "quotation",    label: "Quotation",    hint: "Sales quote wording, validity, item table." },
  { key: "email",        label: "Email",        hint: "Transactional email (Resend). Supports HTML." },
  { key: "whatsapp",     label: "WhatsApp",     hint: "WhatsApp Business text. Keep short; register template name in extras." },
  { key: "pdf",          label: "PDF",          hint: "Generic PDF: reports, statements. Body is HTML → PDF." },
  { key: "notification", label: "Notification", hint: "Internal bell-tray notifications." },
];
const EMPTY = { kind: "email", slug: "", name: "", description: "", subject: "", body: "", is_default: false, enabled: true, extra: {} };

function TemplateDialog({ open, onOpenChange, initial, kind, onSave }) {
  const [f, setF] = useState(EMPTY);
  useEffect(() => {
    setF(initial ? Object.assign({}, EMPTY, initial) : Object.assign({}, EMPTY, { kind }));
  }, [initial, kind, open]);
  const upd = (patch) => setF(Object.assign({}, f, patch));
  const submit = async () => {
    if (!f.name.trim()) return toast.error("Name required");
    if (!f.slug) upd({ slug: f.name.toLowerCase().replace(/[^a-z0-9]+/g, "_") });
    try { await onSave(f); onOpenChange(false); }
    catch (e) { toast.error((e && e.response && e.response.data && e.response.data.detail) || "Save failed"); }
  };
  const showSubject = f.kind === "email" || f.kind === "notification";
  const kindMeta = KINDS.find((k) => k.key === f.kind) || KINDS[0];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="template-dialog">
        <DialogHeader>
          <DialogTitle>{initial && initial.id ? "Edit" : "New"} {kindMeta.label} template</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-xs text-muted-foreground">{kindMeta.hint} Use <code className="font-mono bg-secondary px-1 rounded">{"{{customer.name}}"}</code> placeholders.</p>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">NAME</Label>
              <Input value={f.name} onChange={(e) => upd({ name: e.target.value, slug: f.slug || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "_") })} data-testid="template-name" /></div>
            <div><Label className="text-xs">SLUG</Label>
              <Input className="font-mono" value={f.slug} onChange={(e) => upd({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9_-]+/g, "_") })} data-testid="template-slug" /></div>
          </div>
          <div><Label className="text-xs">DESCRIPTION (internal)</Label>
            <Input value={f.description} onChange={(e) => upd({ description: e.target.value })} /></div>
          {showSubject && (
            <div><Label className="text-xs">SUBJECT / TITLE</Label>
              <Input value={f.subject} onChange={(e) => upd({ subject: e.target.value })}
                placeholder="Invoice {{invoice.number}} from {{company.name}}" data-testid="template-subject" /></div>
          )}
          <div><Label className="text-xs">BODY {f.kind === "email" || f.kind === "pdf" ? "(HTML supported)" : ""}</Label>
            <Textarea rows={f.kind === "whatsapp" ? 4 : 10} value={f.body}
              onChange={(e) => upd({ body: e.target.value })}
              placeholder={sampleFor(f.kind)}
              className={f.kind === "pdf" || f.kind === "email" ? "font-mono text-xs" : ""}
              data-testid="template-body" /></div>
          {f.kind === "whatsapp" && (
            <div><Label className="text-xs">WHATSAPP TEMPLATE NAME (Meta approval)</Label>
              <Input value={(f.extra || {}).template_name || ""}
                onChange={(e) => upd({ extra: Object.assign({}, f.extra || {}, { template_name: e.target.value }) })}
                placeholder="order_shipped_v1" /></div>
          )}
          <div className="flex items-center gap-4 text-xs">
            <label className="flex items-center gap-2"><Switch checked={f.is_default} onCheckedChange={(v) => upd({ is_default: v })} data-testid="template-default" />Default for this kind</label>
            <label className="flex items-center gap-2"><Switch checked={f.enabled} onCheckedChange={(v) => upd({ enabled: v })} />Enabled</label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} data-testid="template-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PreviewDialog({ open, onOpenChange, template }) {
  const [ctx, setCtx] = useState('{"customer":{"name":"Acme Wholesale"},"invoice":{"number":"INV-001","total":45000},"company":{"name":"Vyapar Test Org"}}');
  const [out, setOut] = useState(null);
  useEffect(() => { if (!open) setOut(null); }, [open]);
  const run = async () => {
    let context = {};
    try { context = JSON.parse(ctx); } catch { return toast.error("Invalid JSON context"); }
    try { const r = await api.post("/platform/templates/" + template.id + "/preview", { context }); setOut(r.data); }
    catch (e) { toast.error("Preview failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="template-preview-dialog">
        <DialogHeader><DialogTitle>Preview · {template?.name}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label className="text-xs">CONTEXT JSON</Label>
            <Textarea rows={4} className="font-mono text-xs" value={ctx} onChange={(e) => setCtx(e.target.value)} /></div>
          <Button onClick={run} size="sm" data-testid="template-preview-run">Render preview</Button>
          {out && (
            <div className="space-y-3 pt-2 border-t">
              {out.subject && (<div><Label className="text-xs">SUBJECT</Label>
                <div className="text-sm p-2 rounded border bg-secondary/30">{out.subject}</div></div>)}
              <div><Label className="text-xs">BODY</Label>
                <pre className="text-xs p-3 rounded border bg-secondary/30 whitespace-pre-wrap max-h-60 overflow-y-auto">{out.body}</pre></div>
              <div className="text-[11px] text-muted-foreground">Detected placeholders: {(out.variables || []).map((v) => <code key={v} className="font-mono mr-1">{"{{"}{v}{"}}"}</code>)}</div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function sampleFor(kind) {
  if (kind === "email") return "Hi {{customer.name}},\n\nPlease find attached invoice {{invoice.number}} for ₹{{invoice.total}}.\n\nRegards,\n{{company.name}}";
  if (kind === "whatsapp") return "Hi {{customer.name}}, your order {{order.number}} is out for delivery. Track: {{order.tracking_url}}";
  if (kind === "notification") return "Order {{order.number}} was updated by {{user.name}}.";
  if (kind === "invoice" || kind === "pdf") return "<h1>{{company.name}}</h1><p>Invoice {{invoice.number}} · Total ₹{{invoice.total}}</p>";
  if (kind === "quotation") return "Quotation for {{customer.name}} — valid till {{quote.valid_till}}. Total ₹{{quote.total}}.";
  return "";
}

export default function TemplateBuilder() {
  const [kind, setKind] = useState("invoice");
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [previewing, setPreviewing] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/platform/templates", { params: { kind } }); setTemplates(r.data); }
    catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [kind]);

  const save = async (payload) => {
    if (editing && editing.id) {
      await api.put("/platform/templates/" + editing.id, payload);
      toast.success("Template saved");
    } else {
      await api.post("/platform/templates", payload);
      toast.success("Template created");
    }
    load();
  };
  const remove = async (t) => {
    if (!window.confirm("Delete “" + t.name + "”?")) return;
    await api.delete("/platform/templates/" + t.id);
    toast.success("Deleted"); load();
  };
  const duplicate = async (t) => {
    await api.post("/platform/templates/" + t.id + "/duplicate");
    toast.success("Duplicated"); load();
  };
  const setDefault = async (t) => {
    await api.put("/platform/templates/" + t.id, { is_default: true });
    load();
  };

  return (
    <Card className="p-6" data-testid="template-builder">
      <div className="flex items-start justify-between mb-4 gap-4">
        <div className="min-w-0">
          <div className="overline text-muted-foreground">Templates</div>
          <h3 className="font-heading font-bold text-xl">Template Builder</h3>
          <p className="text-xs text-muted-foreground mt-1">Manage every template the app renders or sends. Use <code className="font-mono bg-secondary px-1 rounded">{"{{customer.name}}"}</code> placeholders — they resolve at send-time from the runtime context.</p>
        </div>
        <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="template-add">
          <Plus size={14} className="mr-1.5" />New template
        </Button>
      </div>
      <div className="flex flex-wrap gap-2 mb-4" data-testid="template-kind-tabs">
        {KINDS.map((k) => (
          <button key={k.key} onClick={() => setKind(k.key)}
            className={"text-xs px-3 py-1.5 rounded-full border " + (kind === k.key ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-secondary")}
            data-testid={"template-kind-" + k.key}>
            {k.label}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="py-12 text-center text-muted-foreground text-sm">Loading…</div>
      ) : (
        <div className="space-y-2">
          {templates.map((t) => (
            <div key={t.id} className={"border rounded-md p-3 flex items-center gap-3 " + (t.enabled ? "" : "opacity-60")} data-testid={"template-row-" + t.slug}>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm flex items-center gap-2">
                  {t.name}
                  <span className="text-[9px] uppercase text-muted-foreground font-mono">{t.slug}</span>
                  {t.is_default && <span className="text-[9px] uppercase text-amber-700 dark:text-amber-300 border border-amber-500/40 px-1 rounded flex items-center gap-1"><Star size={9} weight="fill" />default</span>}
                  {!t.enabled && <span className="text-[9px] uppercase text-muted-foreground border px-1 rounded">disabled</span>}
                </div>
                <div className="text-[11px] text-muted-foreground line-clamp-1">
                  {t.description || t.subject || <span className="italic">no description</span>}
                  {t.variables && t.variables.length > 0 && <> · vars: {t.variables.slice(0, 4).map((v) => <code key={v} className="font-mono mx-0.5">{"{{"+v+"}}"}</code>)}{t.variables.length > 4 && "…"}</>}
                </div>
              </div>
              <button onClick={() => { setPreviewing(t); setPreviewOpen(true); }} className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-primary" title="Preview" data-testid={"template-preview-" + t.slug}>
                <Eye size={13} weight="duotone" />
              </button>
              {!t.is_default && (
                <button onClick={() => setDefault(t)} className="p-1.5 rounded hover:bg-secondary text-muted-foreground" title="Mark default">
                  <Star size={13} weight="duotone" />
                </button>
              )}
              <button onClick={() => duplicate(t)} className="p-1.5 rounded hover:bg-secondary text-muted-foreground" title="Duplicate" data-testid={"template-duplicate-" + t.slug}>
                <Copy size={13} weight="duotone" />
              </button>
              <button onClick={() => { setEditing(t); setDialogOpen(true); }} className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-primary" title="Edit" data-testid={"template-edit-" + t.slug}>
                <PencilSimple size={13} weight="duotone" />
              </button>
              <button onClick={() => remove(t)} className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive" title="Delete" data-testid={"template-delete-" + t.slug}>
                <Trash size={13} weight="duotone" />
              </button>
            </div>
          ))}
          {!templates.length && (
            <div className="py-12 text-center text-muted-foreground text-sm">
              No {kind} templates yet — click “New template”.
            </div>
          )}
        </div>
      )}
      <TemplateDialog open={dialogOpen} onOpenChange={setDialogOpen} initial={editing} kind={kind} onSave={save} />
      {previewing && <PreviewDialog open={previewOpen} onOpenChange={setPreviewOpen} template={previewing} />}
    </Card>
  );
}
